import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, AreaChart, Area,
} from "recharts";
import {
  TrendUp, TrendDown, ChartPieSlice, ChartBar, CurrencyEur, Percent, Files,
  Download, FilePdf, FileXls, X, CalendarBlank, User,
  Buildings, WarningCircle, Plus, Question, ArrowsClockwise, Clock, Minus,
  Lightning,
} from "@phosphor-icons/react";
import { getToken } from "@/lib/authToken";

const PERIODS = [
  { v: 7, l: "7 días" },
  { v: 30, l: "30 días" },
  { v: 90, l: "90 días" },
  { v: 365, l: "1 año" },
];

const METRICS_ALL = [
  { k: "kpis",         label: "Indicadores principales (KPIs)",     desc: "Aperturas, tasación, minuta, margen" },
  { k: "velocidad",    label: "Velocidad de cierre",                 desc: "Días desde encargo hasta finalización" },
  { k: "incidencias",  label: "Incidencias y ampliaciones",          desc: "% de expedientes con incidencias o ampliaciones" },
  { k: "mapfre",       label: "Mapfre — Consultas y Retomas",        desc: "Solo aplica al filtrar por Mapfre" },
  { k: "timeline",     label: "Aperturas por día",                   desc: "Serie temporal de entradas" },
  { k: "aseguradora",  label: "Por compañía + márgenes + velocidad", desc: "Tabla detallada por aseguradora" },
  { k: "causa",        label: "Por causa",                           desc: "Distribución por causa del siniestro" },
  { k: "tipo_encargo", label: "Por tipo de inspección",              desc: "Distribución por tipo de encargo" },
  { k: "perito",       label: "Ranking por perito",                  desc: "Solo cuando no se filtra por un perito concreto" },
];

// Paleta cálida con foco amber + complementarios
const PALETTE = ["#F59E0B", "#0E0F17", "#3B82F6", "#10B981", "#A855F7", "#EF4444", "#06B6D4", "#F97316", "#84CC16", "#EC4899"];

const fmtEur = (n) => (n == null ? "—" : new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n));
const fmtPct = (n) => (n == null ? "—" : `${n}%`);

/**
 * Calcula la variación entre dos valores numéricos.
 * Devuelve null si no hay base de comparación (curr o prev = null/undefined).
 *
 * Tipos:
 *   - `kind="abs"`   → variación absoluta + delta porcentual sobre prev
 *   - `kind="ppt"`   → diferencia en puntos porcentuales (para KPIs ya en %)
 *   - `kind="eur"`   → variación absoluta en euros + delta porcentual
 *   - `kind="days"` → variación absoluta en días con 2 decimales
 */
function computeDelta(curr, prev, kind = "abs") {
  if (curr == null || prev == null) return null;
  const c = Number(curr);
  const p = Number(prev);
  if (Number.isNaN(c) || Number.isNaN(p)) return null;
  const diff = c - p;
  const dir = diff > 0.0001 ? "up" : diff < -0.0001 ? "down" : "flat";
  if (kind === "ppt") {
    return { dir, label: `${diff > 0 ? "+" : ""}${diff.toLocaleString("es-ES", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} pp` };
  }
  if (kind === "days") {
    return { dir, label: `${diff > 0 ? "+" : ""}${diff.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} d` };
  }
  // Para abs/eur: delta % sobre prev. Si prev=0, omitimos %.
  let pct = null;
  if (p !== 0) pct = (diff / p) * 100;
  const absLabel = kind === "eur"
    ? new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(diff)
    : `${diff > 0 ? "+" : ""}${diff}`;
  const pctLabel = pct == null ? null : `${pct > 0 ? "+" : ""}${pct.toFixed(pct.toString().includes(".") || Math.abs(pct) < 10 ? 1 : 0)}%`;
  return { dir, label: pctLabel ? `${absLabel} (${pctLabel})` : absLabel };
}

function KpiDelta({ delta, inverted = false }) {
  if (!delta) {
    return <span className="text-[10px] text-muted italic">sin mes previo</span>;
  }
  // Por defecto subir = positivo (verde). Si `inverted=true` (p.ej. %incidencias),
  // subir es negativo (rojo).
  const isPositive = inverted ? delta.dir === "down" : delta.dir === "up";
  const isNegative = inverted ? delta.dir === "up" : delta.dir === "down";
  const isFlat = delta.dir === "flat";
  const cls = isFlat
    ? "text-muted"
    : isPositive
      ? "text-status-success-fg"
      : "text-status-error-fg";
  const Icon = isFlat ? Minus : (delta.dir === "up" ? TrendUp : TrendDown);
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10.5px] font-semibold ${cls}`} title="Variación vs mes anterior">
      <Icon className="w-3 h-3" weight="bold" />
      {delta.label}
    </span>
  );
}

function Kpi({ label, value, hint, icon: Icon, accent, onClick, dataTestId, delta, deltaInverted }) {
  const Wrap = onClick ? "button" : "div";
  return (
    <Wrap
      onClick={onClick}
      data-testid={dataTestId}
      className={`card-elevated p-5 relative overflow-hidden text-left w-full ${
        onClick ? "cursor-pointer hover:ring-2 hover:ring-brand/30 hover:-translate-y-0.5 transition-all" : ""
      }`}
    >
      <div className="absolute -right-6 -top-6 w-20 h-20 bg-brand/5 rounded-full blur-xl pointer-events-none" />
      <div className="flex items-center justify-between relative">
        <div className="text-[10px] uppercase tracking-widest text-muted font-semibold">{label}</div>
        {Icon && <Icon className={`w-4 h-4 ${accent || "text-muted"}`} weight="bold" />}
      </div>
      <div className="font-display font-black text-3xl tracking-tighter leading-none mt-2.5">{value}</div>
      {delta !== undefined && (
        <div className="mt-1.5" data-testid={dataTestId ? `${dataTestId}-delta` : undefined}>
          <KpiDelta delta={delta} inverted={deltaInverted} />
        </div>
      )}
      {hint && <div className={`text-[11px] text-muted ${delta !== undefined ? "mt-0.5" : "mt-1.5"}`}>{hint}</div>}
      {onClick && (
        <div className="text-[10px] text-brand mt-1.5 font-semibold uppercase tracking-widest">Ver expedientes →</div>
      )}
    </Wrap>
  );
}

function ChartCard({ title, subtitle, icon: Icon, children, right, block }) {
  return (
    <div data-block={block} className="card-elevated p-5">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-display font-bold text-sm tracking-tight inline-flex items-center gap-2">
            {Icon && <Icon className="w-4 h-4 text-brand" weight="duotone" />}
            {title}
          </h3>
          {subtitle && <p className="text-[11px] text-muted mt-0.5">{subtitle}</p>}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function CustomTooltip({ active, payload, label, formatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-foreground text-white rounded-md px-3 py-2 text-xs shadow-lg border border-foreground">
      {label && <div className="font-semibold mb-1">{label}</div>}
      {payload.map((p, i) => (
        <div key={p.name || p.dataKey || i} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ background: p.color || p.payload.fill }} />
          <span className="text-white/70">{p.name}:</span>
          <span className="font-mono">{formatter ? formatter(p.value) : p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function Analytics() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Rol-aware: peritos ven una vista reducida y SIEMPRE filtrada a sí mismos,
  // con periodo "mes en curso desde día 1" por defecto.
  const isPeritoView = ["perito_interno", "perito_externo"].includes(user?.role);
  const dashboardRef = useRef(null);
  const [days, setDays] = useState(90);
  const [customRange, setCustomRange] = useState(isPeritoView);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Por defecto se filtra por el perito que ha iniciado sesión.
  // El usuario puede cambiarlo a "" (vista general gabinete) desde el selector,
  // SALVO en isPeritoView donde el filtro es forzado a su propio code.
  const [perito, setPerito] = useState(user?.code || "");
  const [aseguradoraId, setAseguradoraId] = useState(""); // "" = todas
  const [peritosList, setPeritosList] = useState([]);
  const [aseguradorasList, setAseguradorasList] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportOpen, setExportOpen] = useState(false);
  // Contador Avizor del propio perito (sólo isPeritoView). Mismo rango que
  // el resto de KPIs para no confundir al usuario.
  const [avizorMine, setAvizorMine] = useState(null);

  // Default custom range — peritos: día 1 del mes actual → hoy; resto: últimos 30 días.
  useEffect(() => {
    const today = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    setDateTo(fmt(today));
    if (isPeritoView) {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateFrom(fmt(first));
    } else {
      const past = new Date(); past.setDate(past.getDate() - 30);
      setDateFrom(fmt(past));
    }
  }, [isPeritoView]);

  // Cargar peritos para el selector (una vez)
  useEffect(() => {
    api.get("/peritos")
      .then((r) => setPeritosList(r.data.filter((p) => p.active)))
      .catch(() => setPeritosList([]));
    api.get("/aseguradoras")
      .then((r) => setAseguradorasList((r.data || []).filter((a) => a.active !== false)))
      .catch(() => setAseguradorasList([]));
  }, []);

  const buildQuery = () => {
    const parts = [];
    if (customRange && dateFrom && dateTo) {
      parts.push(`date_from=${dateFrom}`, `date_to=${dateTo}`);
    } else {
      parts.push(`days=${days}`);
    }
    // En isPeritoView el filtro de perito SIEMPRE es el code del user logueado.
    const effectivePerito = isPeritoView ? (user?.code || "") : perito;
    if (effectivePerito) parts.push(`perito=${encodeURIComponent(effectivePerito)}`);
    if (aseguradoraId) parts.push(`aseguradora_id=${encodeURIComponent(aseguradoraId)}`);
    return parts.join("&");
  };

  useEffect(() => {
    setLoading(true);
    api.get(`/analytics?${buildQuery()}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [days, customRange, dateFrom, dateTo, perito, aseguradoraId]);

  // Avizor count para el perito logueado, alineado con el rango actual.
  useEffect(() => {
    if (!isPeritoView) return;
    const q = customRange && dateFrom && dateTo
      ? `date_from=${dateFrom}&date_to=${dateTo}`
      : `days=${days}`;
    api.get(`/analytics/avizor/my-count?${q}`)
      .then((r) => setAvizorMine(r.data || null))
      .catch(() => setAvizorMine(null));
  }, [isPeritoView, days, customRange, dateFrom, dateTo]);

  const totals = data?.totals || {};
  const prev = data?.previous_totals || {};
  const byAseguradora = data?.by_aseguradora || [];
  const byCausa = data?.by_causa || [];
  const byTipoEncargo = data?.by_tipo_encargo || [];
  const byPerito = data?.by_perito || [];
  const timeline = useMemo(() => {
    if (!data?.timeline) return [];
    return data.timeline.map((t) => ({
      ...t,
      label: new Date(t.date).toLocaleDateString("es-ES", { day: "2-digit", month: "short" }),
    }));
  }, [data]);

  const periodLabel = data ? `${data.date_from} → ${data.date_to}` : "...";

  return (
    <div data-testid="analytics-page" className="flex flex-col">
      <header className="border-b border-border bg-white px-6 py-3 flex items-center justify-between sticky top-0 z-20 gap-4">
        <div className="flex-shrink-0">
          <div className="text-[10px] uppercase tracking-widest text-muted">Analítica</div>
          <h1 className="font-display font-bold text-lg tracking-tight">
            Métricas del despacho
            {(isPeritoView ? user?.code : perito) && (
              <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-mono bg-brand-soft text-brand-fg border border-brand/30 px-2 py-0.5 rounded-full uppercase tracking-wider">
                <User className="w-3 h-3" weight="bold" /> {isPeritoView ? user?.code : perito}
              </span>
            )}
            {aseguradoraId && (() => {
              const a = aseguradorasList.find((x) => x.id === aseguradoraId);
              return (
                <span
                  data-testid="aseguradora-pill"
                  className="ml-2 inline-flex items-center gap-1 text-[11px] font-mono bg-blue-50 text-blue-700 border border-blue-300 px-2 py-0.5 rounded-full uppercase tracking-wider">
                  <Buildings className="w-3 h-3" weight="bold" /> {a?.name || a?.code || ""}
                </span>
              );
            })()}
          </h1>
        </div>

        <div className="flex items-center gap-3 flex-wrap justify-end">
          {/* Aseguradora selector */}
          <div className="inline-flex items-center gap-1.5 bg-surface border border-border rounded-md pl-2">
            <Buildings className="w-3.5 h-3.5 text-muted" weight="bold" />
            <select
              value={aseguradoraId}
              onChange={(e) => setAseguradoraId(e.target.value)}
              data-testid="aseguradora-filter"
              className="!text-xs !py-1 !pl-1 !pr-6 !border-0 !bg-transparent !shadow-none focus:!ring-0"
              title="Filtrar por aseguradora"
            >
              <option value="">Todas las aseguradoras</option>
              {aseguradorasList.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>

          {/* Perito selector — los peritos ven SIEMPRE su propia data, sin selector */}
          {!isPeritoView && (
            <div className="inline-flex items-center gap-1.5 bg-surface border border-border rounded-md pl-2">
              <User className="w-3.5 h-3.5 text-muted" weight="bold" />
              <select
                value={perito}
                onChange={(e) => setPerito(e.target.value)}
                data-testid="perito-filter"
                className="!text-xs !py-1 !pl-1 !pr-6 !border-0 !bg-transparent !shadow-none focus:!ring-0"
                title="Filtrar por perito"
              >
                <option value="">Gabinete (todos)</option>
                {peritosList.map((p) => (
                  <option key={p.id} value={p.code}>{p.code} — {p.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Period selector + custom — los peritos ven SIEMPRE mes en curso */}
          {!isPeritoView && (
          <div className="inline-flex items-center gap-1 bg-surface rounded-md p-0.5 border border-border">
            {PERIODS.map((p) => (
              <button
                key={p.v}
                onClick={() => { setCustomRange(false); setDays(p.v); }}
                data-testid={`period-${p.v}`}
                className={`text-xs px-3 py-1 rounded font-medium transition-colors ${
                  !customRange && days === p.v
                    ? "bg-white text-foreground shadow-sm"
                    : "text-muted hover:text-foreground"
                }`}
              >{p.l}</button>
            ))}
            <button
              onClick={() => setCustomRange(true)}
              data-testid="period-custom"
              className={`text-xs px-3 py-1 rounded font-medium transition-colors inline-flex items-center gap-1 ${
                customRange ? "bg-white text-foreground shadow-sm" : "text-muted hover:text-foreground"
              }`}
            >
              <CalendarBlank className="w-3 h-3" weight="bold" /> Custom
            </button>
          </div>
          )}
          {isPeritoView && (
            <div className="inline-flex items-center gap-1.5 bg-brand-soft border border-brand/30 text-brand-fg rounded-md px-3 py-1 text-xs font-semibold">
              <CalendarBlank className="w-3 h-3" weight="bold" /> Mes en curso
            </div>
          )}

          {customRange && !isPeritoView && (
            <div className="inline-flex items-center gap-2 animate-fade-in-down">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="date-from"
                className="!text-xs !py-1 !px-2 !w-36"
              />
              <span className="text-muted text-xs">→</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="date-to"
                className="!text-xs !py-1 !px-2 !w-36"
              />
            </div>
          )}

          <button
            onClick={() => setExportOpen(true)}
            data-testid="open-export"
            className="btn-brand inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-semibold"
          >
            <Download className="w-3.5 h-3.5" weight="bold" /> Exportar
          </button>
        </div>
      </header>

      <div ref={dashboardRef} className="p-6 space-y-6 animate-fade-in">
        {loading && !data ? (
          <div className="p-12 text-center text-muted">Cargando métricas...</div>
        ) : (
          <>
            {/* KPIs */}
            <div data-block="kpis" className={`grid grid-cols-2 ${isPeritoView ? "lg:grid-cols-3" : "lg:grid-cols-6"} gap-4`}>
              <Kpi
                label="Aperturas"
                value={totals.expedientes ?? 0}
                hint={isPeritoView ? "este mes" : `últimos ${days} días`}
                icon={Files}
                accent="text-brand"
                dataTestId="kpi-aperturas"
                delta={isPeritoView ? computeDelta(totals.expedientes, prev.expedientes, "abs") : undefined}
              />
              {isPeritoView ? (
                <>
                  <Kpi
                    label="Coste medio"
                    value={fmtEur(totals.tasacion_avg)}
                    hint="€ media tasada por siniestro"
                    icon={CurrencyEur}
                    dataTestId="kpi-coste-medio"
                    delta={computeDelta(totals.tasacion_avg, prev.tasacion_avg, "eur")}
                  />
                  <Kpi
                    label="Velocidad cierre"
                    value={totals.velocidad_cierre_count
                      ? `${(totals.velocidad_cierre_avg_days ?? 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} d`
                      : "—"}
                    hint={totals.velocidad_cierre_count
                      ? `${totals.velocidad_cierre_count} cerrados`
                      : "sin cerrados"}
                    icon={Clock}
                    accent="text-cyan-600"
                    dataTestId="kpi-velocidad-cierre"
                    delta={prev.velocidad_cierre_count
                      ? computeDelta(totals.velocidad_cierre_avg_days, prev.velocidad_cierre_avg_days, "days")
                      : undefined}
                    deltaInverted
                  />
                </>
              ) : (
                <>
                  <Kpi label="Subidos al ERP" value={totals.submitted ?? 0} hint="completados" icon={TrendUp} />
                  <Kpi label="Tasación media" value={fmtEur(totals.tasacion_avg)} hint="€ daños tasados" icon={CurrencyEur} />
                  <Kpi label="Minuta media" value={fmtEur(totals.minuta_avg)} hint="€ honorarios" icon={CurrencyEur} />
                  <Kpi label="Margen medio" value={fmtPct(totals.margen_pct)} hint={`${fmtEur(totals.margen_total)} total`} icon={Percent} accent="text-status-success-fg" />
                  <Kpi
                    label="Velocidad cierre"
                    value={totals.velocidad_cierre_count ? `${totals.velocidad_cierre_avg_days} d` : "—"}
                    hint={totals.velocidad_cierre_count
                      ? `media (${totals.velocidad_cierre_count} cerrados · md ${totals.velocidad_cierre_median_days}d · ${totals.velocidad_cierre_min_days}-${totals.velocidad_cierre_max_days}d)`
                      : "sin datos"}
                    icon={Clock}
                    accent="text-cyan-600"
                    dataTestId="kpi-velocidad-cierre"
                  />
                </>
              )}
            </div>

            {/* KPIs cualitativos: incidencias, ampliaciones (+ Mapfre: consultas, retomas) (+ Avizor: perito) */}
            <div data-block="incidencias" className={`grid grid-cols-2 ${totals.is_mapfre ? "lg:grid-cols-4" : (isPeritoView && avizorMine ? "lg:grid-cols-3" : "lg:grid-cols-2")} gap-4`}>
              <Kpi
                label="% Incidencias"
                value={fmtPct(totals.pct_incidencias)}
                hint={`${totals.incidencias_count ?? 0} de ${totals.expedientes ?? 0}`}
                icon={WarningCircle}
                accent="text-amber-600"
                dataTestId="kpi-incidencias"
                delta={isPeritoView ? computeDelta(totals.pct_incidencias, prev.pct_incidencias, "ppt") : undefined}
                deltaInverted
                onClick={() => {
                  const params = new URLSearchParams({ fase: "incidencias" });
                  if (aseguradoraId) params.set("aseguradora_id", aseguradoraId);
                  navigate(`/inbox?${params.toString()}`);
                }}
              />
              <Kpi
                label="% Ampliaciones"
                value={fmtPct(totals.pct_ampliaciones)}
                hint={`${totals.ampliaciones_count ?? 0} de ${totals.expedientes ?? 0}`}
                icon={Plus}
                accent="text-blue-600"
                dataTestId="kpi-ampliaciones"
                delta={isPeritoView ? computeDelta(totals.pct_ampliaciones, prev.pct_ampliaciones, "ppt") : undefined}
                deltaInverted
                onClick={() => {
                  const params = new URLSearchParams({ fase: "ampliaciones" });
                  if (aseguradoraId) params.set("aseguradora_id", aseguradoraId);
                  navigate(`/inbox?${params.toString()}`);
                }}
              />
              {totals.is_mapfre && (
                <>
                  <Kpi
                    label="% Consultas"
                    value={fmtPct(totals.pct_consultas)}
                    hint={`${totals.consultas_count ?? 0} de ${totals.expedientes ?? 0} (Mapfre)`}
                    icon={Question}
                    accent="text-purple-600"
                    dataTestId="kpi-consultas"
                    delta={isPeritoView ? computeDelta(totals.pct_consultas, prev.pct_consultas, "ppt") : undefined}
                    deltaInverted
                    onClick={() => {
                      const params = new URLSearchParams({
                        tipo_encargo: "Consulta",
                        aseguradora_id: aseguradoraId,
                      });
                      navigate(`/inbox?${params.toString()}`);
                    }}
                  />
                  <Kpi
                    label="% Retomas"
                    value={fmtPct(totals.pct_retomas)}
                    hint={`${totals.retomas_count ?? 0} de ${totals.expedientes ?? 0} (Mapfre)`}
                    icon={ArrowsClockwise}
                    accent="text-emerald-600"
                    dataTestId="kpi-retomas"
                    delta={isPeritoView ? computeDelta(totals.pct_retomas, prev.pct_retomas, "ppt") : undefined}
                    deltaInverted
                    onClick={() => {
                      const params = new URLSearchParams({
                        tipo_encargo: "Retom",
                        aseguradora_id: aseguradoraId,
                      });
                      navigate(`/inbox?${params.toString()}`);
                    }}
                  />
                </>
              )}
              {/* KPI Avizor para el perito: aceptados en el periodo + ratio. */}
              {isPeritoView && avizorMine && (
                <Kpi
                  label="Avizores atendidos"
                  value={avizorMine.accepted ?? 0}
                  hint={`${avizorMine.received ?? 0} recibidos · ${avizorMine.rejected ?? 0} rech. · ${avizorMine.expired ?? 0} exp.`}
                  icon={Lightning}
                  accent="text-amber-600"
                  dataTestId="kpi-avizor-mine"
                />
              )}
            </div>

            {/* Timeline */}
            <ChartCard
              block="timeline"
              title="Aperturas por día"
              subtitle="Últimos 30 días"
              icon={ChartBar}
            >
              {timeline.length === 0 ? (
                <EmptyChart label="Sin aperturas en este periodo" />
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={timeline} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colCount" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.5} />
                        <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="hsl(230 8% 92%)" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" stroke="hsl(230 6% 60%)" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="hsl(230 6% 60%)" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="count" name="Aperturas" stroke="#F59E0B" strokeWidth={2} fillOpacity={1} fill="url(#colCount)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* By Aseguradora */}
              <ChartCard
                block="aseguradora"
                title="Aperturas por compañía"
                subtitle={`${byAseguradora.length} aseguradoras con actividad`}
                icon={ChartBar}
              >
                {byAseguradora.length === 0 ? (
                  <EmptyChart label="Sin datos" />
                ) : (
                  <ResponsiveContainer width="100%" height={Math.max(220, byAseguradora.length * 30)}>
                    <BarChart data={byAseguradora} layout="vertical" margin={{ top: 5, right: 16, left: 8, bottom: 0 }}>
                      <CartesianGrid stroke="hsl(230 8% 92%)" strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" stroke="hsl(230 6% 60%)" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                      <YAxis dataKey="name" type="category" stroke="hsl(230 6% 60%)" fontSize={11} tickLine={false} axisLine={false} width={110} />
                      <Tooltip content={<CustomTooltip />} cursor={{ fill: "hsl(40 30% 96%)" }} />
                      <Bar dataKey="count" name="Expedientes" fill="#F59E0B" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>

              {/* By Causa */}
              <ChartCard
                block="causa"
                title="Por causa"
                subtitle="Distribución de tipos de siniestro"
                icon={ChartPieSlice}
              >
                {byCausa.length === 0 ? (
                  <EmptyChart label="Sin datos" />
                ) : (
                  <div className="grid grid-cols-[1fr_140px] items-center gap-4">
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={byCausa}
                          dataKey="count"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={90}
                          paddingAngle={2}
                          stroke="white"
                          strokeWidth={2}
                        >
                          {byCausa.map((c, i) => (
                            <Cell key={c.name} fill={PALETTE[i % PALETTE.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-1.5">
                      {byCausa.map((c, i) => (
                        <div key={c.name} className="flex items-center gap-2 text-[11px]">
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: PALETTE[i % PALETTE.length] }} />
                          <span className="truncate flex-1 text-muted">{c.name}</span>
                          <span className="font-mono font-semibold">{c.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </ChartCard>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* By Tipo Encargo */}
              <ChartCard
                block="tipo_encargo"
                title="Por tipo de inspección"
                subtitle="Presencial / Videoperitación / Telemático / Juicio"
                icon={ChartPieSlice}
              >
                {byTipoEncargo.length === 0 ? (
                  <EmptyChart label="Sin datos" />
                ) : (
                  <div className="grid grid-cols-[1fr_140px] items-center gap-4">
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={byTipoEncargo}
                          dataKey="count"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={90}
                          paddingAngle={2}
                          stroke="white"
                          strokeWidth={2}
                        >
                          {byTipoEncargo.map((c, i) => (
                            <Cell key={c.name} fill={PALETTE[i % PALETTE.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-1.5">
                      {byTipoEncargo.map((c, i) => (
                        <div key={c.name} className="flex items-center gap-2 text-[11px]">
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: PALETTE[i % PALETTE.length] }} />
                          <span className="truncate flex-1 text-muted">{c.name}</span>
                          <span className="font-mono font-semibold">{c.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </ChartCard>

              {/* Importes y márgenes por aseguradora — sólo admin/gestor_senior */}
              {!isPeritoView && (
              <ChartCard
                block="margen_aseguradora"
                title="Margen por compañía"
                subtitle="Tasación media (€) y margen sobre minuta"
                icon={CurrencyEur}
              >
                {byAseguradora.length === 0 ? (
                  <EmptyChart label="Sin datos económicos" />
                ) : (
                  <div className="overflow-y-auto max-h-72">
                    <table className="w-full text-[12px]">
                      <thead className="thead-modern">
                        <tr>
                          <th>Compañía</th>
                          <th className="!text-right">Aperturas</th>
                          <th className="!text-right">Tasación medio</th>
                          <th className="!text-right">Minuta media</th>
                          <th className="!text-right">Margen</th>
                          <th className="!text-right">Veloc. cierre</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byAseguradora.map((a, idx) => (
                          <tr key={a.name} className={`border-t border-[#F1F3F5] hover:bg-[#F5F6F8] transition ${idx % 2 ? "bg-[#FBFBFC]" : ""}`}>
                            <td className="py-2.5 px-3 truncate max-w-[140px]">{a.name}</td>
                            <td className="py-2.5 px-3 text-right tabular-nums">{a.count}</td>
                            <td className="py-2.5 px-3 text-right tabular-nums text-muted">{a.tasacion_avg ? fmtEur(a.tasacion_avg) : "—"}</td>
                            <td className="py-2.5 px-3 text-right tabular-nums text-muted">{a.minuta_avg ? fmtEur(a.minuta_avg) : "—"}</td>
                            <td className={`py-2.5 px-3 text-right tabular-nums font-semibold ${a.margen_pct > 0 ? "text-status-success-fg" : "text-muted"}`}>
                              {a.margen_pct ? fmtPct(a.margen_pct) : "—"}
                            </td>
                            <td className="py-2.5 px-3 text-right tabular-nums text-cyan-700">
                              {a.velocidad_avg_days != null
                                ? <span title={`Media · ${a.velocidad_count} cerrados`}>{a.velocidad_avg_days}d</span>
                                : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </ChartCard>
              )}
            </div>

            {/* Ranking por perito — solo en vista general (admin/gestor) y sin filtro de perito */}
            {!perito && !isPeritoView && (
              <ChartCard
                block="perito"
                title="Ranking por perito"
                subtitle={`${byPerito.length} peritos con actividad · click en cualquiera para filtrar`}
                icon={User}
              >
                {byPerito.length === 0 ? (
                  <EmptyChart label="Sin actividad de peritos en este periodo" />
                ) : (
                  <div className="overflow-x-auto -mx-2">
                    <table className="w-full text-[12px]">
                      <thead className="thead-modern">
                        <tr>
                          <th>Perito</th>
                          <th className="!text-right">Aperturas</th>
                          <th className="!text-right">Subidos ERP</th>
                          <th className="!text-right">Tasación media</th>
                          <th className="!text-right">Minuta media</th>
                          <th className="!text-right">Minuta total</th>
                          <th className="!text-right">Margen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byPerito.map((p, idx) => (
                          <tr
                            key={p.code}
                            data-testid={`perito-row-${p.code}`}
                            onClick={() => p.code !== "SIN_ASIGNAR" && setPerito(p.code)}
                            className={`border-t border-[#F1F3F5] transition-colors ${
                              p.code === "SIN_ASIGNAR"
                                ? "opacity-60"
                                : "cursor-pointer hover:bg-[#FDF0DB]/40"
                            } ${idx % 2 ? "bg-[#FBFBFC]" : ""}`}
                          >
                            <td className="py-2.5 px-3">
                              <div className="text-[11.5px] font-semibold uppercase tabular-nums">{p.code}</div>
                              <div className="text-[10.5px] text-muted">{p.name}</div>
                            </td>
                            <td className="py-2.5 px-3 text-right tabular-nums font-semibold">{p.count}</td>
                            <td className="py-2.5 px-3 text-right tabular-nums text-muted">{p.submitted}</td>
                            <td className="py-2.5 px-3 text-right tabular-nums text-muted">{p.tasacion_avg ? fmtEur(p.tasacion_avg) : "—"}</td>
                            <td className="py-2.5 px-3 text-right tabular-nums text-muted">{p.minuta_avg ? fmtEur(p.minuta_avg) : "—"}</td>
                            <td className="py-2.5 px-3 text-right tabular-nums">{p.minuta_total ? fmtEur(p.minuta_total) : "—"}</td>
                            <td className={`py-2.5 px-3 text-right tabular-nums font-semibold ${p.margen_pct > 0 ? "text-status-success-fg" : "text-muted"}`}>
                              {p.margen_pct ? fmtPct(p.margen_pct) : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </ChartCard>
            )}

            {/* Sección Avizor — sólo en vista general (admin/gestor).
                Mide volumen, tasas de aceptación y desglose por gestor /
                perito / aseguradora. Vive bajo el mismo selector de periodo. */}
            {!isPeritoView && (
              <AvizorSection
                days={days}
                customRange={customRange}
                dateFrom={dateFrom}
                dateTo={dateTo}
              />
            )}

            {/* Hint */}
            <div className="text-[11px] text-muted leading-relaxed bg-brand-soft border border-brand/20 rounded-md px-4 py-3">
              💡 <strong className="text-brand-fg">Tip:</strong> los importes de tasación, minuta y coste se rellenan en cada expediente
              (pestaña Datos · sección Económico). Cuanto más completes esta info, más precisos serán los márgenes y comparativas.
            </div>
          </>
        )}
      </div>

      {exportOpen && (
        <ExportModal
          dashboardRef={dashboardRef}
          dateFrom={customRange && dateFrom ? dateFrom : defaultFrom(days)}
          dateTo={customRange && dateTo ? dateTo : new Date().toISOString().slice(0, 10)}
          perito={perito}
          aseguradoraId={aseguradoraId}
          aseguradoraName={(aseguradorasList.find((a) => a.id === aseguradoraId) || {}).name || ""}
          onClose={() => setExportOpen(false)}
        />
      )}
    </div>
  );
}

function defaultFrom(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

const EXPORT_BLOCKS = [
  { k: "kpis",                label: "KPIs principales",          desc: "Aperturas, tasación, minuta, margen, velocidad" },
  { k: "incidencias",         label: "KPIs cualitativos",         desc: "Incidencias, ampliaciones, consultas/retomas" },
  { k: "timeline",            label: "Aperturas por día",         desc: "Serie temporal" },
  { k: "aseguradora",         label: "Aperturas por compañía",    desc: "Gráfico de barras" },
  { k: "causa",               label: "Por causa",                 desc: "Distribución de tipos de siniestro" },
  { k: "tipo_encargo",        label: "Por tipo de inspección",    desc: "Presencial / Vídeo / Telemático / Juicio" },
  { k: "margen_aseguradora",  label: "Margen por compañía",       desc: "Tabla con tasación / minuta / margen" },
  { k: "perito",              label: "Ranking por perito",        desc: "Solo si no estás filtrando por un perito" },
];

function ExportModal({ dashboardRef, dateFrom, dateTo, perito, aseguradoraId, aseguradoraName, onClose }) {
  const [format, setFormat] = useState("pdf");
  const [downloading, setDownloading] = useState(false);
  const [stage, setStage] = useState("");
  const [selected, setSelected] = useState(() => EXPORT_BLOCKS.map((b) => b.k));
  const token = getToken();

  const toggle = (k) =>
    setSelected((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  // Detecta los bloques presentes en el DOM (algunos solo se renderizan según filtros)
  const availableBlocks = EXPORT_BLOCKS.filter((b) => {
    if (b.k === "perito" && perito) return false;
    return true;
  });

  const captureDashboard = async () => {
    const el = dashboardRef?.current;
    if (!el) throw new Error("No se encontró el dashboard a exportar");
    // Ocultar bloques no seleccionados antes de la captura
    const allBlocks = el.querySelectorAll("[data-block]");
    const previous = [];
    allBlocks.forEach((node) => {
      const k = node.getAttribute("data-block");
      if (!selected.includes(k)) {
        previous.push([node, node.style.display]);
        node.style.display = "none";
      }
    });
    try {
      const html2canvas = (await import("html2canvas")).default;
      return await html2canvas(el, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        logging: false,
        windowWidth: el.scrollWidth,
        windowHeight: el.scrollHeight,
      });
    } finally {
      previous.forEach(([node, prev]) => { node.style.display = prev; });
    }
  };

  const buildFilenameSuffix = () => {
    const parts = [];
    if (perito) parts.push(perito.toUpperCase());
    if (aseguradoraName) parts.push(aseguradoraName.replace(/\s+/g, "_"));
    return parts.length ? `_${parts.join("_")}` : "";
  };

  const downloadPdf = async () => {
    setStage("Capturando dashboard...");
    const canvas = await captureDashboard();
    setStage("Generando PDF...");
    const { jsPDF } = await import("jspdf");
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const marginX = 6;
    const usableW = pageW - marginX * 2;
    const ratio = canvas.height / canvas.width;
    const fullH = usableW * ratio;
    let y = 0;
    let pageIdx = 0;
    while (y < fullH) {
      if (pageIdx > 0) pdf.addPage();
      pdf.addImage(imgData, "PNG", marginX, -y, usableW, fullH, undefined, "FAST");
      y += pageH;
      pageIdx += 1;
    }
    const fname = `dobilas_dashboard${buildFilenameSuffix()}_${dateFrom}_${dateTo}.pdf`;
    pdf.save(fname);
  };

  const downloadXlsx = async () => {
    setStage("Capturando dashboard...");
    const canvas = await captureDashboard();
    setStage("Generando Excel...");
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
    const fd = new FormData();
    fd.append("snapshot", blob, "dashboard.png");
    fd.append("date_from", dateFrom);
    fd.append("date_to", dateTo);
    if (perito) fd.append("perito", perito);
    if (aseguradoraId) fd.append("aseguradora_id", aseguradoraId);
    fd.append("metrics", selected.join(","));
    const res = await fetch(`${API}/analytics/export/xlsx-snapshot`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    if (!res.ok) throw new Error("Error generando el Excel");
    const fileBlob = await res.blob();
    const cd = res.headers.get("Content-Disposition") || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    const filename = m ? m[1] : `dobilas_dashboard${buildFilenameSuffix()}.xlsx`;
    const a = document.createElement("a");
    const objUrl = URL.createObjectURL(fileBlob);
    a.href = objUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objUrl);
  };

  const download = async () => {
    if (selected.length === 0) { alert("Selecciona al menos un bloque"); return; }
    setDownloading(true);
    try {
      if (format === "pdf") await downloadPdf();
      else await downloadXlsx();
      onClose();
    } catch (e) {
      console.error(e);
      alert(e.message || "Error en la exportación");
    } finally {
      setDownloading(false);
      setStage("");
    }
  };

  const scopeLabel = perito ? `Perito ${perito}` : "Gabinete completo";
  const asegLabel = aseguradoraName || "Todas las aseguradoras";

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="export-modal"
    >
      <div
        className="bg-white rounded-lg w-full max-w-xl shadow-2xl animate-fade-in max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-4 border-b border-border">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-display font-bold text-xl tracking-tight">Exportar dashboard</h2>
            <button onClick={onClose} className="text-muted hover:text-foreground p-1" data-testid="close-export">
              <X className="w-4 h-4" weight="bold" />
            </button>
          </div>
          <p className="text-xs text-muted">
            Captura fiel del dashboard. Elige los bloques que quieres incluir.
          </p>
          <div className="flex flex-wrap gap-1.5 mt-3">
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-foreground text-white font-semibold">{scopeLabel}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-semibold">{asegLabel}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-mono">{dateFrom} → {dateTo}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">{selected.length}/{availableBlocks.length} bloques</span>
          </div>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1">
          <label className="!normal-case !tracking-normal !text-[11px] !font-semibold !text-muted">Formato</label>
          <div className="grid grid-cols-2 gap-2 mt-1.5">
            {[
              { v: "pdf", icon: FilePdf, label: "PDF Dashboard", desc: "Captura fiel del dashboard" },
              { v: "xlsx", icon: FileXls, label: "Excel", desc: "Captura + datos crudos editables" },
            ].map(({ v, icon: Icon, label, desc }) => (
              <button
                key={v}
                onClick={() => setFormat(v)}
                data-testid={`format-${v}`}
                className={`p-3 rounded-md border text-left transition-all ${
                  format === v
                    ? "border-brand bg-brand/5 ring-2 ring-brand/20"
                    : "border-border hover:border-border-strong bg-white"
                }`}
              >
                <Icon className={`w-5 h-5 mb-1.5 ${format === v ? "text-brand" : "text-muted"}`} weight="duotone" />
                <div className="font-semibold text-sm">{label}</div>
                <div className="text-[10px] text-muted">{desc}</div>
              </button>
            ))}
          </div>

          <div className="mt-5">
            <div className="flex items-center justify-between">
              <label className="!normal-case !tracking-normal !text-[11px] !font-semibold !text-muted">
                Bloques a incluir ({selected.length}/{availableBlocks.length})
              </label>
              <div className="flex gap-2 text-[10px]">
                <button
                  onClick={() => setSelected(availableBlocks.map((b) => b.k))}
                  className="text-muted hover:text-brand"
                  data-testid="export-select-all"
                >Todos</button>
                <span className="text-border">·</span>
                <button
                  onClick={() => setSelected([])}
                  className="text-muted hover:text-brand"
                  data-testid="export-select-none"
                >Ninguno</button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
              {availableBlocks.map((b) => {
                const isOn = selected.includes(b.k);
                return (
                  <button
                    key={b.k}
                    type="button"
                    onClick={() => toggle(b.k)}
                    data-testid={`block-${b.k}`}
                    className={`text-left p-3 rounded-md border transition-all ${
                      isOn
                        ? "border-brand bg-brand/5 ring-2 ring-brand/20"
                        : "border-border hover:border-border-strong bg-white"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={isOn}
                        readOnly
                        className="!w-3.5 !h-3.5 !p-0 !mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm leading-tight">{b.label}</div>
                        {b.desc && <div className="text-[10px] text-muted mt-0.5">{b.desc}</div>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-border bg-surface/30">
          <button
            onClick={onClose}
            disabled={downloading}
            className="text-xs px-4 py-2 text-muted hover:text-foreground"
          >Cancelar</button>
          <button
            onClick={download}
            disabled={downloading || selected.length === 0}
            data-testid="export-download"
            className="btn-brand inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-md font-semibold disabled:opacity-50"
          >
            {downloading ? (
              <span className="inline-flex items-center gap-2">
                <span className="dot-blink"><span/><span/><span/></span>
                {stage || "Procesando..."}
              </span>
            ) : (
              <><Download className="w-3.5 h-3.5" weight="bold" /> Descargar</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyChart({ label }) {
  return (
    <div className="h-48 flex items-center justify-center text-sm text-muted">
      {label}
    </div>
  );
}

// =================================================================
// AvizorSection — KPIs + breakdowns por gestor / perito / aseguradora
// =================================================================
function AvizorSection({ days, customRange, dateFrom, dateTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const q = customRange && dateFrom && dateTo
      ? `date_from=${dateFrom}&date_to=${dateTo}`
      : `days=${days}`;
    api.get(`/analytics/avizor?${q}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days, customRange, dateFrom, dateTo]);

  if (loading || !data || data.forbidden) return null;
  const t = data.totals || {};
  if (!t.launched) {
    return (
      <section data-testid="avizor-analytics-empty" className="card-base p-6">
        <div className="flex items-center gap-2 mb-2">
          <span className="icon-chip bg-amber-100 text-amber-800">
            <Lightning className="w-4 h-4" weight="fill" />
          </span>
          <h3 className="font-display text-base font-bold">Avizor</h3>
        </div>
        <p className="text-[12px] text-muted">Sin actividad de Avizor en este periodo.</p>
      </section>
    );
  }

  return (
    <section data-testid="avizor-analytics-section" className="card-base p-6 space-y-5">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="icon-chip bg-amber-100 text-amber-800">
            <Lightning className="w-4 h-4" weight="fill" />
          </span>
          <div>
            <h3 className="font-display text-base font-bold tracking-tight">Avizor</h3>
            <p className="text-[11px] text-muted">Lanzamientos, ratios y desglose por gestor / perito / aseguradora.</p>
          </div>
        </div>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <AvizorKPI label="Lanzados" value={t.launched} testid="avizor-kpi-launched" />
        <AvizorKPI label="Aceptados" value={t.accepted} sub={`${t.pct_accepted}%`} tone="success" testid="avizor-kpi-accepted" />
        <AvizorKPI label="Rechazados" value={t.rejected} sub={`${t.pct_rejected}%`} tone="error" testid="avizor-kpi-rejected" />
        <AvizorKPI label="Expirados" value={t.expired} sub={`${t.pct_expired}%`} tone="muted" testid="avizor-kpi-expired" />
      </div>

      {/* Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <AvizorTable
          title="Por gestor (quién lanza)"
          rows={data.by_gestor || []}
          cols={[
            { k: "name", l: "Gestor" },
            { k: "launched", l: "Lanzados", num: true },
            { k: "accepted", l: "Aceptados", num: true },
            { k: "pct_accepted", l: "% Acept.", num: true, suffix: "%" },
          ]}
          testid="avizor-by-gestor"
        />
        <AvizorTable
          title="Por perito (quién acepta)"
          rows={data.by_perito || []}
          cols={[
            { k: "name", l: "Perito" },
            { k: "received", l: "Recibidos", num: true },
            { k: "accepted", l: "Aceptados", num: true },
            { k: "pct_accepted", l: "% Acept.", num: true, suffix: "%" },
          ]}
          testid="avizor-by-perito"
        />
        <AvizorTable
          title="Por aseguradora"
          rows={data.by_aseguradora || []}
          cols={[
            { k: "name", l: "Aseguradora" },
            { k: "launched", l: "Lanzados", num: true },
          ]}
          testid="avizor-by-aseguradora"
        />
      </div>
    </section>
  );
}

function AvizorKPI({ label, value, sub, tone, testid }) {
  const toneCls = tone === "success" ? "text-status-success-fg"
                : tone === "error"   ? "text-status-error-fg"
                : tone === "muted"   ? "text-muted"
                : "text-foreground";
  return (
    <div data-testid={testid} className="border border-border rounded-md px-3 py-2.5 bg-white">
      <div className="text-[10px] uppercase tracking-widest text-muted font-semibold">{label}</div>
      <div className={`text-2xl font-bold tabular-nums leading-tight ${toneCls}`}>{value}</div>
      {sub != null && <div className="text-[11px] text-muted tabular-nums">{sub}</div>}
    </div>
  );
}

function AvizorTable({ title, rows, cols, testid }) {
  return (
    <div data-testid={testid} className="border border-border rounded-md overflow-hidden bg-white">
      <div className="px-3 py-2 text-[11px] uppercase tracking-wider font-bold text-muted border-b border-border bg-surface/40">
        {title}
      </div>
      {rows.length === 0 ? (
        <div className="px-3 py-4 text-[11.5px] text-muted text-center italic">Sin datos</div>
      ) : (
        <ul className="divide-y divide-border/40">
          {rows.slice(0, 10).map((row, idx) => (
            <li key={`${row.id || row.name}-${idx}`} className="px-3 py-1.5 grid items-center gap-2"
                style={{ gridTemplateColumns: `1fr ${"60px ".repeat(cols.length - 1)}` }}>
              {cols.map((c, ci) => (
                <span
                  key={c.k}
                  className={`text-[11.5px] ${ci === 0 ? "truncate font-medium text-foreground" : "text-right tabular-nums text-muted"}`}
                >
                  {row[c.k] == null ? "—" : `${row[c.k]}${c.suffix || ""}`}
                </span>
              ))}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
